import React from "react";
import { Slider } from "@/components/ui/slider";
import { formatINR } from "@/lib/format";

function OptionRow({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 py-1.5 text-left text-sm transition-colors ${
        active ? "text-gold" : "text-ink hover:text-gold"
      }`}
    >
      <span className={`grid h-4 w-4 place-items-center rounded-full border ${active ? "border-gold" : "border-border"}`}>
        {active && <span className="h-2 w-2 rounded-full bg-gold" />}
      </span>
      {label}
    </button>
  );
}

function Group({ title, children }) {
  return (
    <div className="border-b border-border py-6 first:pt-0">
      <h3 className="text-luxe text-[11px] font-semibold text-ink mb-3">{title}</h3>
      {children}
    </div>
  );
}

export function FilterSidebar({ facets, filters, onChange, onClear }) {
  if (!facets) return null;
  const set = (key, value) => onChange({ [key]: filters[key] === value ? "all" : value });

  const priceMin = facets.min_price ?? 0;
  const priceMax = facets.max_price ?? 200000;
  const range = [filters.minPrice ?? priceMin, filters.maxPrice ?? priceMax];

  return (
    <div data-testid="filter-sidebar">
      <div className="flex items-center justify-between pb-4">
        <h2 className="font-serif text-2xl text-ink">Filters</h2>
        <button onClick={onClear} className="text-xs uppercase tracking-wider text-muted-foreground hover:text-gold">
          Clear all
        </button>
      </div>

      <Group title="Category">
        <OptionRow label="All Jewellery" active={!filters.category || filters.category === "all"} onClick={() => onChange({ category: "all" })} />
        {facets.categories.map((c) => (
          <OptionRow key={c} label={c} active={filters.category === c} onClick={() => set("category", c)} />
        ))}
      </Group>

      <Group title="Material">
        <OptionRow label="All Materials" active={!filters.material || filters.material === "all"} onClick={() => onChange({ material: "all" })} />
        {facets.materials.map((m) => (
          <OptionRow key={m} label={m} active={filters.material === m} onClick={() => set("material", m)} />
        ))}
      </Group>

      <Group title="Collection">
        <OptionRow label="All Collections" active={!filters.collection || filters.collection === "all"} onClick={() => onChange({ collection: "all" })} />
        {facets.collections.map((c) => (
          <OptionRow key={c.handle} label={c.title} active={filters.collection === c.handle} onClick={() => set("collection", c.handle)} />
        ))}
      </Group>

      <Group title="Availability">
        <OptionRow label="All" active={!filters.availability || filters.availability === "all"} onClick={() => onChange({ availability: "all" })} />
        <OptionRow label="In Stock" active={filters.availability === "in_stock"} onClick={() => set("availability", "in_stock")} />
        <OptionRow label="Out of Stock" active={filters.availability === "out_of_stock"} onClick={() => set("availability", "out_of_stock")} />
      </Group>

      <Group title="Price">
        <Slider
          min={priceMin}
          max={priceMax}
          step={1000}
          value={range}
          onValueChange={(v) => onChange({ minPrice: v[0], maxPrice: v[1] })}
          className="mt-2"
        />
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>{formatINR(range[0])}</span>
          <span>{formatINR(range[1])}</span>
        </div>
      </Group>
    </div>
  );
}
